/**
 * <guna-table> — the eight kūṭas, each with its score, its maximum and its reason.
 *
 * ◆ **No percentage, no badge, no colour that reads as a grade.** The total is
 * shown as the plain sum it is, and the verdict is the prose gunaMilan() wrote.
 * A number out of 36 is a computation about two Moon positions, not a decision
 * about two people (12-matching.md § 12.2).
 *
 * ⚠ A kūṭa that was waived by a classical exception says so first, in its own
 * row, before the score. And the tables behind the scores are not from a primary
 * text — the component always shows that.
 *
 * Set `.result` (from gunaMilan) — optionally `.names = { bride, groom }`.
 *
 * © Manish Jagdish Thatte
 */

import { h, nz } from '../utils/dom.js';

export class GunaTable extends HTMLElement {
  connectedCallback() { this.render(); }

  set result(value) { this._result = value; this.render(); }
  get result() { return this._result ?? null; }

  render() {
    this.textContent = '';
    const r = this._result;
    if (!r) return;

    const fmt = (n) => nz(Number.isInteger(n) ? n : n.toFixed(1));

    const rows = r.kutas.map((k) => h('tr', { class: k.waived ? 'is-waived' : null },
      h('th', { scope: 'row' }, k.name.en, h('span', { class: 'guna-table__sa', lang: 'sa' }, ` ${k.name.sa}`)),
      h('td', { class: 'is-numeric' }, `${fmt(k.score)} / ${fmt(k.max)}`),
      h('td', {},
        k.waived ? h('span', { class: 'chip' }, 'classical exception applied') : null,
        h('span', { class: 'guna-table__reason' }, k.reason))));

    this.append(h('div', { class: 'table-scroll' }, h('table', { class: 'data-table guna-table' },
      h('caption', {}, 'Aṣṭakūṭa — bride first, groom second'),
      h('thead', {}, h('tr', {}, ['Kūṭa', 'Score', 'Reason'].map((c) => h('th', { scope: 'col' }, c)))),
      h('tbody', {}, rows),
      h('tfoot', {}, h('tr', {}, h('th', { scope: 'row' }, 'Sum of the eight'),
        h('td', { class: 'is-numeric' }, `${fmt(r.total)} / ${fmt(r.max)}`), h('td', {}))))));

    this.append(h('p', { class: 'prose guna-table__verdict' }, r.verdict));

    if (r.mangala) this.append(this.mangala(r.mangala));

    if (!r.verified) {
      this.append(h('div', { class: 'notice notice--info' },
        h('p', {}, 'The score tables here are the commonly reproduced ones and have not been checked against a primary text. Still open:'),
        h('ul', { class: 'list' }, r.unverified.map((u) => h('li', {}, u)))));
    }
    for (const n of r.notes) this.append(h('p', { class: 'field-note' }, n));
  }

  mangala(m) {
    const one = (who, d) => {
      const bits = d.views.map((v) => {
        if (!v.available) return `from the ${v.from}: not assessed (no birth time)`;
        if (!v.present) return `from the ${v.from}: Maṅgala is in the ${v.house}th, not one of the houses`;
        return `from the ${v.from}: Maṅgala is in the ${v.house}th` +
          (v.cancellations.length ? ` — cancelled: ${v.cancellations.join('; ')}` : ' — no cancellation found');
      });
      return h('li', {}, h('strong', {}, `${who}: `),
        d.present ? (d.cancelled ? 'noted, and cancelled. ' : 'noted. ') : 'not present. ', bits.join('; '), '.');
    };
    return h('div', { class: 'panel' },
      h('h3', {}, 'Maṅgala Doṣa (kept out of the 36)'),
      h('ul', { class: 'list' }, one('Bride', m.bride), one('Groom', m.groom)),
      h('p', { class: 'field-note' }, m.bride.note));
  }
}

customElements.define('guna-table', GunaTable);
